import { apiClient } from "../../../lib/api-client";
import { API_PATHS } from "../../../config/api";
import {
  CreateTestBuildPayload,
  CreateTestBuildResult,
  TestBuild,
} from "../../../types/testBuild";

/** Server limit for `content_length`, in kilobytes (= 512 MB). */
export const MAX_TEST_BUILD_SIZE_KB = 524288;

export interface TestBuildPresign {
  presign: { url: string; fields: Record<string, string> };
  serverFileName: string;
}

/**
 * The test-build endpoints double-nest some payloads inside the standard
 * `{ success, code, message, data }` envelope that `apiClient` already unwraps,
 * so a few methods here reach one more level into `data.data`. That extra hop
 * is absorbed in this file on purpose — no component should ever see it.
 */
export const testBuildService = {
  /**
   * Step 1 of 3. `content_length` is in KILOBYTES, not bytes: sending bytes
   * rejects anything over ~512 KB with a confusing "Maximum file size
   * exceeded". The returned presign is valid for 180 seconds, so call this at
   * submit time and start the S3 upload immediately — never on dialog open.
   */
  generateUploadUrl: async (
    projectId: string | number,
    file: File
  ): Promise<TestBuildPresign> => {
    const response = await apiClient.post<{
      data: { url: string; fields: Record<string, string> };
      serverFileName: string;
    }>(API_PATHS.TEST_BUILD_GENERATE_UPLOAD_URL, {
      projectId,
      content_length: Math.ceil(file.size / 1024),
      filename: file.name,
    });
    return { presign: response.data, serverFileName: response.serverFileName };
  },

  /**
   * Step 3, after the S3 upload succeeds. Calling this twice with the same
   * variant + version does not overwrite — you get iteration 1, then 2, and
   * both remain listed.
   */
  createBuild: async (
    payload: CreateTestBuildPayload
  ): Promise<CreateTestBuildResult> => {
    return apiClient.post(API_PATHS.TEST_BUILD_ITEM, {
      ...payload,
      versionCode: String(payload.versionCode),
    });
  },

  /**
   * Already sorted newest-first, with expired builds filtered out server-side.
   *
   * Note the request key is `project_id` here while every other test-build
   * endpoint takes `projectId`. That is the server contract, not a typo.
   */
  listBuilds: async (projectId: string | number): Promise<TestBuild[]> => {
    const response = await apiClient.post<{ data: TestBuild[] }>(
      API_PATHS.TEST_BUILD_ITEMS,
      { project_id: projectId }
    );
    return Array.isArray(response?.data) ? response.data : [];
  },

  getBuild: async (id: string): Promise<TestBuild> => {
    return apiClient.post(API_PATHS.TEST_BUILD_DETAIL, { id });
  },

  /**
   * Resolves to a plain URL string, not an object. Valid 180 seconds — trigger
   * the download straight away rather than storing it in a link.
   *
   * A 404 here means the build is no longer available, and expiry is by far the
   * likeliest cause: the server deliberately does not distinguish expired from
   * missing from not-yours.
   */
  getDownloadUrl: async (id: string): Promise<string> => {
    return apiClient.post(API_PATHS.TEST_BUILD_GENERATE_DOWNLOAD_URL, { id });
  },

  /** Deletes the record and the binary. Irreversible, no trash. */
  deleteBuild: async (id: string): Promise<void> => {
    return apiClient.post(API_PATHS.TEST_BUILD_DELETE_ITEM, { id });
  },
};

export default testBuildService;
